import {computed, type Ref, ref} from "vue";
import type {App} from "vue";
import api from "../api/index.js";
import type {
    BookingPref,
    Category, ConiCard, Difficulty, Exercise, ExtraSeatsPause, Genre, Holiday,
    KitDisplay,
    Media, Product, Purchase, Settings, Subcategory, User, UserSeat
} from "@/models";


const getDefaultSettings = ():Settings => {
    if(localStorage.settings) {
        try {
            return  JSON.parse(localStorage.settings);
        } catch (e) {
            console.log("settings in localstorage parse err", e)
        }
    }
    return {
        coni_card_price: 15,
        home_kit_price: 60,
        home_kit_expedition_price: 12,
        home_kit_expedition_days: 7,
        purchase_expiration_days_after_first_use: 45,
        last_update: "2024-09-05T07:02:20.340Z",
        open_days: {
            lun: true, mar: true, mer: true, gio: true, ven: true, sab: true, dom: false
        },
        user_bookings_per_week: 7,
        user_bookings_per_day: 1,
        bookings_per_hour_exp: 12,
        bookings_per_hour_corsi: 8,
        bookings_per_hour_macchine: 25,
        bookings_per_hour_vacu: 1,
        bookings_per_hour_gym: 4,
        cancel_within_minutes: 120,
        booking_within_minutes: 15,
        booking_within_minutes_exp: 0,
        booking_within_minutes_corsi: 0,
        booking_within_minutes_macchine: 0,
        booking_within_minutes_vacu: 0,
        booking_within_minutes_gym: 0,
        holidays: {
            natale: true, santo_stefano: true, capodanno: true, epifania: false, pasquetta: true, liberazione: true,
            lavoro: true, repubblica: true, ferragosto: true, santi: true, immacolata: true
        },
        day_of_sport_year_start: 1,
        month_of_sport_year_start: 9,
        max_days_of_subscription_pause: 10,
        extra_day_of_sub_pause_price: 3,
        banner_duration: 5
    }
}

interface GlobalRefs {
    media:Ref<Media[]>
    kits:Ref<KitDisplay[]>
    products:Ref<Product[]>
    purchases:Ref<Purchase[]>
    userseats:Ref<UserSeat[]>
    users:Ref<User[]>
    categories:Ref<Category[]>
    genre:Ref<Genre[]>
    difficulties:Ref<Difficulty[]>
    subcategories:Ref<Subcategory[]>
    exercises:Ref<Exercise[]>
    pauses:Ref<ExtraSeatsPause[]>
    cards:Ref<ConiCard[]>
    holidays:Ref<Holiday[]>
    bookingprefs:Ref<BookingPref[]>
}

type GlobalRefsKey = keyof GlobalRefs;


class Globals implements GlobalRefs {
    private initialized = false
    title = ref('')


    media = ref<Media[]>([])
    kits = ref<KitDisplay[]>([])
    products = ref<Product[]>([])
    purchases = ref<Purchase[]>([])
    userseats = ref<UserSeat[]>([])
    users = ref<User[]>([])
    categories = ref<Category[]>([])
    genre = ref<Genre[]>([])
    difficulties = ref<Difficulty[]>([])
    subcategories = ref<Subcategory[]>([])
    exercises = ref<Exercise[]>([])
    pauses = ref<ExtraSeatsPause[]>([])
    cards = ref<ConiCard[]>([])
    holidays = ref<Holiday[]>([])
    bookingprefs = ref<BookingPref[]>([])
    settings = ref<Settings>(getDefaultSettings())


    async refresh(collection:GlobalRefsKey, sort?:string):Promise<void> {
        if(!sort) sort = undefined;
        let res = await api.list(collection, {}, 0, 0, sort)
        if(res.success) {
            this[collection].value = res.data ? res.data as any[] : [];
        }
    }

    async refreshEmpty(collection:GlobalRefsKey, sort?:string):Promise<void> {
        if(!this[collection].value.length) {
            return this.refresh(collection, sort);
        }
    }

    async loadSettings():Promise<void> {
        let res = await api.get<Settings>('settings')
        if(res.success) {
            localStorage.settings = JSON.stringify(res.data!)
            this.settings.value = res.data!;
        }
    }

    async init() {
        await Promise.all([
            this.refresh('products'),
            this.loadSettings()
        ])
        this.initialized = true;
    }
    public get waitForInit(): Promise<void> {
        return new Promise((res) => {
            if(this.initialized) {
                res()
                return;
            }
            let loop = setInterval(() => {
                if (this.initialized) {
                    res();
                    clearInterval(loop);
                }
            }, 250);
        })
    }
}


export default {
    install: (app:App) => {
        const glob = new Globals()
        app.provide<Globals>("globals", glob)
    },
};

export { Globals }
